import { Key, useCallback, useMemo } from "react"
import {
  Block,
  FlexGrid,
  FlexGridItem,
  HeadingMedium,
  Link,
  ParagraphLarge,
  ParagraphMedium,
  useStyletron,
} from "@mezo-org/mezo-clay"

import depositCoins from "#/assets/deposit-coins.png"
import {
  selectActiveDeposits,
  selectIsConnected,
  setOpenModal,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import { IS_MEZO_MAINNET } from "#/shared/constants"
import { networkUtils } from "#/shared/utils"

import DepositEmptyStateButton from "./DepositEmptyStateButton"

type DepositEmptyStateProps = {
  onClick: (key: Key) => void
}

export default function DepositEmptyState({ onClick }: DepositEmptyStateProps) {
  const activeDeposits = useDappSelector(selectActiveDeposits)
  const isConnected = useDappSelector(selectIsConnected)
  const dispatch = useDappDispatch()

  const [css, theme] = useStyletron()

  const signInHandler = useCallback(() => {
    dispatch(setOpenModal("onboarding"))
  }, [dispatch])

  const hasActiveDeposits = useMemo(
    () => activeDeposits.length > 0 && isConnected,
    [activeDeposits, isConnected],
  )

  if (isConnected && !hasActiveDeposits) {
    // TODO: Add better content
    return "No deposits"
  }

  return (
    <Block
      className={css({
        background: theme.colors.backgroundTertiary,
        padding: theme.sizing.scale1200,
        borderRadius: theme.borders.radius500,
        overflow: "hidden",
      })}
    >
      <FlexGrid
        flexGridColumnCount={2}
        flexGridColumnGap={theme.sizing.scale3200}
        alignItems="center"
      >
        <FlexGridItem>
          <HeadingMedium marginBottom={theme.sizing.scale600}>
            {IS_MEZO_MAINNET ? (
              "Start your adventure!"
            ) : (
              <>
                Start your adventure with a <br />
                deposit!
              </>
            )}
          </HeadingMedium>
          <ParagraphLarge marginBottom={theme.sizing.scale900}>
            {IS_MEZO_MAINNET
              ? "Start earning and explore the Mezo world!"
              : `Add your Bitcoin to start earning mats and explore the ${networkUtils.getMezoDAppLabel()} world!`}
          </ParagraphLarge>
          <DepositEmptyStateButton onClick={onClick} />
          <Block
            marginTop={theme.sizing.scale700}
            height={theme.sizing.scale800}
          >
            {!IS_MEZO_MAINNET && !isConnected && (
              <ParagraphMedium as="div" color={theme.colors.contentTertiary}>
                Already have an account?{" "}
                <Link $as="button" href="/" onClick={signInHandler}>
                  Sign in
                </Link>
              </ParagraphMedium>
            )}
          </Block>
        </FlexGridItem>
        <FlexGridItem position="relative">
          <Block
            as="img"
            src={depositCoins}
            width="168px"
            className={css({
              position: "absolute",
              top: "-240px",
              left: "20%",
            })}
          />
        </FlexGridItem>
      </FlexGrid>
    </Block>
  )
}
